import { useCallback, useEffect, useState } from 'react';
import { locationsAPI, readersAPI } from '../services/api';
import { Bus, Landmark, MapPin, Pencil, Plus, ScanFace, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const emptyReaderForm = {
  reader_uid: '',
  name: '',
  location_id: '',
  reader_admin_pin: '1234',
  device_type: 'rfid',
  checkpoint_type: 'main_gate',
};

const checkpointOptions = [
  { value: 'main_gate', label: 'Main Gate' },
  { value: 'bus', label: 'Bus' },
  { value: 'classroom', label: 'Classroom' },
  { value: 'office', label: 'Office' },
  { value: 'other', label: 'Other' },
];

const getCheckpointLabel = (value) => checkpointOptions.find((item) => item.value === value)?.label || 'Main Gate';

const ReaderStatusBadge = ({ reader }) => {
  const isOnlineRecently = reader.last_seen
    ? (Date.now() - new Date(reader.last_seen).getTime()) < 5 * 60 * 1000
    : false;

  if (!reader.is_active) {
    return <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Inactive</span>;
  }

  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${isOnlineRecently ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
      {isOnlineRecently ? 'Online' : 'Idle'}
    </span>
  );
};

const Readers = () => {
  const { hasPermission } = useAuth();
  const [readers, setReaders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showReaderForm, setShowReaderForm] = useState(false);
  const [savingReader, setSavingReader] = useState(false);
  const [editingReaderId, setEditingReaderId] = useState(null);
  const [readerForm, setReaderForm] = useState(emptyReaderForm);

  const loadReaders = useCallback(async () => {
    const [readersRes, locationsRes] = await Promise.all([
      readersAPI.getAll(),
      locationsAPI.getAll(),
    ]);

    setReaders(readersRes.data.readers);
    setLocations(locationsRes.data.locations);
  }, []);

  useEffect(() => {
    loadReaders().catch(() => toast.error('Failed to load readers'));
  }, [loadReaders]);

  const handleSaveReader = async (event) => {
    event.preventDefault();

    if (!readerForm.reader_uid.trim()) {
      toast.error('Reader UID is required');
      return;
    }

    try {
      setSavingReader(true);
      if (editingReaderId) {
        await readersAPI.update(editingReaderId, {
          name: readerForm.name.trim(),
          location_id: readerForm.location_id || null,
          device_type: readerForm.device_type,
          checkpoint_type: readerForm.checkpoint_type,
        });
        toast.success('Reader updated successfully');
      } else {
        await readersAPI.create({
          reader_uid: readerForm.reader_uid.trim(),
          name: readerForm.name.trim(),
          location_id: readerForm.location_id || null,
          reader_admin_pin: readerForm.reader_admin_pin.trim() || '1234',
          device_type: readerForm.device_type,
          checkpoint_type: readerForm.checkpoint_type,
        });
        toast.success('Reader added successfully');
      }
      setReaderForm(emptyReaderForm);
      setEditingReaderId(null);
      setShowReaderForm(false);
      await loadReaders();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add reader');
    } finally {
      setSavingReader(false);
    }
  };

  const startEditReader = (reader) => {
    setEditingReaderId(reader.id);
    setReaderForm({
      reader_uid: reader.reader_uid || '',
      name: reader.name || '',
      location_id: reader.location_id || '',
      reader_admin_pin: reader.reader_admin_pin || '1234',
      device_type: reader.device_type || 'rfid',
      checkpoint_type: reader.checkpoint_type || 'main_gate',
    });
    setShowReaderForm(true);
  };

  const handleRemoveReader = async (readerId) => {
    try {
      await readersAPI.remove(readerId);
      toast.success('Reader removed successfully');
      await loadReaders();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove reader');
    }
  };

  const handleChangePin = async (reader) => {
    const nextPin = window.prompt(`Enter new 4-digit admin PIN for ${reader.reader_uid}`, reader.reader_admin_pin || '1234');

    if (nextPin === null) {
      return;
    }

    if (!/^\d{4}$/.test(nextPin.trim())) {
      toast.error('Admin PIN must be exactly 4 digits');
      return;
    }

    try {
      await readersAPI.updatePin(reader.id, { reader_admin_pin: nextPin.trim() });
      toast.success('Reader PIN updated successfully');
      await loadReaders();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update reader PIN');
    }
  };

  const canManage = hasPermission('readers.manage');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Readers</h1>
          <p className="text-gray-500 text-sm mt-1">Add new readers, assign locations, and manage device access.</p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowReaderForm((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus size={16} />
            {showReaderForm ? 'Close Form' : 'Add Reader'}
          </button>
        )}
      </div>

      {showReaderForm && canManage && (
        <form onSubmit={handleSaveReader} className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Device Type</label>
            <select
              value={readerForm.device_type}
              onChange={(e) => setReaderForm({ ...readerForm, device_type: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="rfid">RFID Reader</option>
              <option value="face">Face Recognition Tablet</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Checkpoint Type</label>
            <select
              value={readerForm.checkpoint_type}
              onChange={(e) => setReaderForm({ ...readerForm, checkpoint_type: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {checkpointOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Reader UID</label>
            <input
              type="text"
              value={readerForm.reader_uid}
              onChange={(e) => setReaderForm({ ...readerForm, reader_uid: e.target.value.toUpperCase() })}
              placeholder={readerForm.device_type === 'face' ? 'FACE-TABLET-001' : 'READER-004'}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={Boolean(editingReaderId)}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Reader Name</label>
            <input
              type="text"
              value={readerForm.name}
              onChange={(e) => setReaderForm({ ...readerForm, name: e.target.value })}
              placeholder="Main Gate Tablet Reader"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Location</label>
            <select
              value={readerForm.location_id}
              onChange={(e) => setReaderForm({ ...readerForm, location_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No location</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Admin PIN</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={readerForm.reader_admin_pin}
              onChange={(e) => setReaderForm({ ...readerForm, reader_admin_pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
              placeholder="1234"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="md:col-span-3 flex gap-3">
            <button
              type="submit"
              disabled={savingReader}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {savingReader ? 'Saving...' : editingReaderId ? 'Update Reader' : 'Save Reader'}
            </button>
            <button
              type="button"
              onClick={() => {
                setReaderForm(emptyReaderForm);
                setEditingReaderId(null);
                setShowReaderForm(false);
              }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        {readers.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No readers found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Reader</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Checkpoint</th>
                <th className="px-4 py-3 text-left">UID</th>
                <th className="px-4 py-3 text-left">Location</th>
                <th className="px-4 py-3 text-left">Department</th>
                <th className="px-4 py-3 text-left">Last Seen</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Admin PIN</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {readers.map((reader) => (
                <tr key={reader.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{reader.name || 'Unnamed Reader'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                      reader.device_type === 'face'
                        ? 'bg-teal-100 text-teal-700'
                        : 'bg-slate-100 text-slate-700'
                    }`}>
                      {reader.device_type === 'face' ? <ScanFace size={12} /> : null}
                      {reader.device_type === 'face' ? 'Face' : 'RFID'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                      reader.checkpoint_type === 'bus'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {reader.checkpoint_type === 'bus' ? <Bus size={12} /> : <Landmark size={12} />}
                      {getCheckpointLabel(reader.checkpoint_type)}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{reader.reader_uid}</td>
                  <td className="px-4 py-3 text-gray-600">
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-gray-400" />
                      <span>{reader.location_name || '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{reader.department_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {reader.last_seen ? new Date(reader.last_seen).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <ReaderStatusBadge reader={reader} />
                  </td>
                  <td className="px-4 py-3 text-gray-600">Configured</td>
                  <td className="px-4 py-3">
                    {reader.is_active && canManage ? (
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => startEditReader(reader)}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <Pencil size={12} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleChangePin(reader)}
                          className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50"
                        >
                          Change PIN
                        </button>
                        <button
                          onClick={() => handleRemoveReader(reader.id)}
                          className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={12} />
                          Remove
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">{reader.is_active ? 'View only' : 'Removed'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Readers;
